// src/services/userManageService.js

import api from '../fapis/client';
import endpoints from '../fapis/endpoints';

export const userManageService = {
  /**
   * Get all users
   */
  getUsers: async () => {
    try {
      const response = await api.get(endpoints.manage_user.list);
      return {
        success: response.data.success || false,
        data: response.data.users || [],
        total: response.data.total || 0,
        company: response.data.company || null, 
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get users error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch users',
        status: error.status || 500
      };
    }
  },

  /**
   * Get single user
   */
  getUser: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'User ID is required',
        status: 400
      };
    }

    try {
      const response = await api.get(endpoints.manage_user.get(uuid));
      return {
        success: response.data.success || false,
        data: response.data.user || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Get user ${uuid} error:`, error);
      return {
        success: false,
        message: error.message || 'Failed to fetch user',
        status: error.status || 500
      };
    }
  },

  /**
   * Update user role
   */
  updateUserRole: async (uuid, roleId) => {
  if (!uuid) {
    return {
      success: false,
      message: 'User ID is required',
      status: 400
    };
  }

  if (!roleId) {
    return {
      success: false,
      message: 'Role ID is required',
      status: 400
    };
  }

  try {
    const response = await api.patch(
      endpoints.manage_user.updateRole(uuid),
      { role_id: roleId }
    );
    return {
      success: response.data.success || false,
      message: response.data.message || 'User role updated',
      data: response.data.user || null,
      status: response.status
    };
  } catch (error) {
    console.error(`❌ Update user ${uuid} role error:`, error);
    
    let message = 'Failed to update user role';
    let status = error.status || 500;
    
    // Extract error message from response
    if (error.response) {
      status = error.response.status;
      message = error.response.data?.detail || error.response.data?.message || error.message;
    } else if (error.status) {
      status = error.status;
      message = error.message || 'Invalid role';
    }
    
    // User-friendly messages
    if (status === 403) message = 'You do not have permission to change this user\'s role';
    else if (status === 404) message = 'User not found';
    else if (status === 400) message = message || 'Invalid role selected';

    return {
      success: false,
      message: message,
      status: status
    };
  }
},

  /**
   * Suspend user
   */
suspendUser: async (uuid, signal) => {
  if (!uuid) {
    return {
      success: false,
      message: 'User ID is required',
      status: 400
    };
  }

  try {
    const response = await api.post(
      endpoints.manage_user.suspend(uuid),
      {},
      { signal }  // Pass abort signal to the request
    );
    return {
      success: response.data.success || false,
      message: response.data.message || 'User suspended',
      data: response.data.user || null,
      status: response.status
    };
  } catch (error) {
    // If aborted, don't log or process
    if (error.name === 'AbortError') {
      return {
        success: false,
        message: 'Request cancelled',
        status: 499
      };
    }
    
    console.error(`❌ Suspend user ${uuid} error:`, error);
    
    let message = 'Failed to suspend user';
    let status = error.status || 500;
    
    if (error.response) {
      status = error.response.status;
      message = error.response.data?.detail || error.response.data?.message || error.message;
    }

    if (status === 403) message = 'You do not have permission to suspend this user';
    else if (status === 404) message = 'User not found';
    else if (status === 400) message = message || 'User cannot be suspended';

    return {
      success: false,
      message: message,
      status: status
    };
  }
},

  /**
   * Reactivate user
   */
  reactivateUser: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'User ID is required',
        status: 400
      };
    }

    try {
      const response = await api.post(endpoints.manage_user.reactivate(uuid));
      return {
        success: response.data.success || false,
        message: response.data.message || 'User reactivated',
        data: response.data.user || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Reactivate user ${uuid} error:`, error);
      
      let message = 'Failed to reactivate user';
      if (error.status === 403) message = error.message || 'Permission denied';
      else if (error.status === 404) message = 'User not found';
      else if (error.status === 400) message = error.message || 'User cannot be reactivated';

      return {
        success: false,
        message: message,
        status: error.status || 500
      };
    }
  },

  /**
   * Delete user (soft delete)
   */
  deleteUser: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'User ID is required',
        status: 400
      };
    }

    try {
      const response = await api.delete(endpoints.manage_user.delete(uuid));
      return {
        success: response.data.success || false,
        message: response.data.message || 'User deleted',
        data: response.data.user || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Delete user ${uuid} error:`, error);
      
      let message = 'Failed to delete user';
      if (error.status === 403) message = error.message || 'Permission denied';
      else if (error.status === 404) message = 'User not found';
      else if (error.status === 400) message = error.message || 'User cannot be deleted';

      return {
        success: false,
        message: message,
        status: error.status || 500
      };
    }
  },

  /**
   * Get all roles
   */
  /**
 * Get all roles
 */
getRoles: async () => {
  try {
    const response = await api.get(endpoints.retailone.roles);
    // Response is directly an array, not wrapped in { success: true, data: [...] }
    return {
      success: true,
      data: response.data || [],
      status: response.status
    };
  } catch (error) {
    console.error('❌ Get roles error:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch roles',
      status: error.status || 500
    };
  }
},
};

export default userManageService;